import { Search as SearchIcon } from 'lucide-react';
import {
  Box,
  Modal,
  ModalBody,
  Text,
  TextInput,
  useDisclosure,
  VStack
} from 'pixelize-design-library';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppTheme } from '@core/Theme/useAppTheme';
import { mutedText, surface } from '@core/Theme/tokens';
import { navItemsForRole } from '@core/layout/navigation';
import { useAppSelector } from '@core/store/hooks';

/**
 * Ctrl/Cmd-K jump list. Its entries come from `core/layout/navigation.ts`, the same model
 * the sidebar reads — the two used to be separate hand-written tables and had drifted, so
 * pages existed in one and not the other.
 */
export function GlobalSearchPalette() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useAppTheme();
  const role = useAppSelector((s) => s.auth.user?.role);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onOpen]);

  const items = useMemo(() => {
    const term = query.trim().toLowerCase();
    return navItemsForRole(role)
      .map((item) => ({ path: item.path, label: t(item.labelKey) }))
      .filter((item) => !term || item.label.toLowerCase().includes(term));
  }, [role, query, t]);

  const close = () => {
    onClose();
    setQuery('');
  };

  const go = (path: string) => {
    navigate(path);
    close();
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size='xl'>
      <ModalBody>
        <Box bg={surface(colors)} borderRadius='xl' data-testid='global-search-palette'>
          <Box borderBottomWidth='1px' borderColor={colors.boxborder[200]}>
            <TextInput
              autoFocus
              size='lg'
              variant='flushed'
              placeholder={t('common.searchPages')}
              aria-label={t('common.searchPages')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              inputLeftIcon={<SearchIcon size={18} aria-hidden />}
              inputStyle={{ borderBottomWidth: 0 }}
              data-testid='global-search-input'
            />
          </Box>

          <VStack align='stretch' spacing={0} maxH='60vh' overflowY='auto' py={2}>
            {items.length === 0 ? (
              <Text
                px={4}
                py={3}
                color={mutedText(colors)}
                fontSize='sm'
                data-testid='global-search-empty'
              >
                {t('common.noResults')}
              </Text>
            ) : (
              items.map((item) => (
                <Box
                  key={item.path}
                  px={4}
                  py={3}
                  mx={2}
                  cursor='pointer'
                  borderRadius='md'
                  _hover={{ bg: colors.backgroundColor.light }}
                  onClick={() => go(item.path)}
                  data-testid={`global-search-item-${item.path}`}
                >
                  <Text fontSize='sm' fontWeight='medium' color={colors.text[900]}>
                    {item.label}
                  </Text>
                  <Text fontSize='xs' color={mutedText(colors)}>
                    {item.path}
                  </Text>
                </Box>
              ))
            )}
          </VStack>

          <Box px={4} py={2} borderTopWidth='1px' borderColor={colors.boxborder[200]}>
            <Text fontSize='xs' color={mutedText(colors)}>
              {t('search.escToClose')}
            </Text>
          </Box>
        </Box>
      </ModalBody>
    </Modal>
  );
}
